import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBill1,
  faCalendarAlt,
  faLocationDot,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Modal from "../../components/modal/modal";

const All_Highest_Sallary_Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [likedJobs, setLikedJobs] = useState([]);
  const [modalMessage, setModalMessage] = useState(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const userId = user?.id;

  // 🔹 Получаем вакансии
  const fetchJobs = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await fetch(
        `/api/get_highest_salary_vacancies.php?page=${pageNumber}&limit=20`
      );
      const data = await res.json();
      if (data.success) {
        setJobs(data.vacancies);
        setTotalPages(data.totalPages);
        setPage(pageNumber);
      }
    } catch (err) {
      console.error("Ошибка при получении вакансий:", err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Получаем лайки пользователя
  useEffect(() => {
    if (!userId) return;
    const fetchLikes = async () => {
      try {
        const res = await fetch(
          `/api/get_liked_vacancies.php?user_id=${userId}`
        );
        const data = await res.json();
        // Проверяем структуру ответа
        setLikedJobs(
          Array.isArray(data)
            ? data.map((v) => Number(v.id))
            : data.liked?.map((v) => Number(v.id)) || []
        );
      } catch (err) {
        console.error("Ошибка при получении лайков:", err);
      }
    };
    fetchLikes();
  }, [userId]);

  useEffect(() => {
    fetchJobs(1);
  }, []);

  // 🔹 Обработчик лайка
  const handleLike = async (jobId) => {
    if (!user) {
      setModalMessage({
        title: t("attention"),
        ok: t("ok"),
        text: t("pleaseregister"),
        success: true,
        onClose: () => navigate("/WorkerChoose"),
      });
      localStorage.setItem("pending_like", jobId);
      return;
    }

    const alreadyLiked = likedJobs.includes(Number(jobId));

    try {
      const res = await fetch("/api/toggle_like.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          vacancy_id: jobId,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setLikedJobs((prev) =>
          alreadyLiked
            ? prev.filter((id) => id !== Number(jobId))
            : [...prev, Number(jobId)]
        );
      }
    } catch (err) {
      console.error("Ошибка при лайке:", err);
    }
  };

  // 🔹 Проверка pending_like после регистрации
  useEffect(() => {
    const pending = localStorage.getItem("pending_like");
    if (user && pending) {
      handleLike(Number(pending));
      localStorage.removeItem("pending_like");
    }
  }, [user]);

  if (loading) {
    return (
      <div className="w-full flex justify-center items-center h-[200px]">
        <p className="text-[#006674] font-semibold animate-pulse text-xl">
          {t("loading")}...
        </p>
      </div>
    );
  }

  return (
    <section className="w-full flex flex-col items-center p-6 gap-6 opacity-0 animate-[showtitle_1s_forwards]">
      <h1 className="text-3xl uppercase font-bold text-white mb-4">
        {t("Highestpayingjobs")}
      </h1>

      {modalMessage && (
        <Modal
          title={modalMessage.title}
          text={modalMessage.text}
          ok={modalMessage.ok}
          success={modalMessage.success}
          onClose={() => {
            if (modalMessage.onClose) modalMessage.onClose();
            setModalMessage(null);
          }}
        />
      )}

      {jobs.length === 0 ? (
        <p className="text-white text-lg">
          Нет вакансий с указанной зарплатой.
        </p>
      ) : (
        <div className="w-full flex flex-wrap justify-center gap-8">
          {jobs.map((job) => {
            const isLiked = likedJobs.includes(Number(job.id));

            return (
              <article
                key={job.id}
                className="w-[440px] pt-3 overflow-hidden flex-col relative flex justify-start gap-3 items-center h-auto rounded-lg shadow-2xl shadow-black cursor-pointer bg-white"
              >
                <div className="w-full px-3 flex justify-start items-center gap-6">
                  <img
                    className="w-[100px] rounded-xl border-[4px] bg-[#bdbdbd] border-white h-[100px] object-contain shadow-2xl shadow-neutral-400"
                    src={
                      job.companyLogo
                        ? `/api/uploads/${job.companyLogo}`
                        : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                    }
                    alt={job.companyName}
                  />
                  <ul className="flex flex-col gap-1 items-start justify-center">
                    <li>
                      <Link
                        to={`/vacancy/${job.id}`}
                        className="text-xl text-[#006674] font-semibold hover:underline"
                      >
                        {job.workname}
                      </Link>
                    </li>
                    <li>{job.companyName}</li>
                    <li className="flex gap-2 justify-start items-center text-gray-700">
                      <FontAwesomeIcon icon={faLocationDot} />
                      {job.workplace || "Не указано"}
                    </li>
                    <li className="flex gap-2 justify-start items-center text-gray-700">
                      <FontAwesomeIcon icon={faMoneyBill1} />
                      {job.salary
                        ? `${job.salary} ${job.currency || ""}`
                        : "Договорная"}
                    </li>
                  </ul>
                </div>

                <button
                  onClick={() => handleLike(job.id)}
                  className={`absolute right-2 top-24 rounded-full border-[2px] border-white shadow-lg shadow-neutral-500 transition-all duration-200 w-[35px] h-[35px] ${
                    isLiked
                      ? "bg-red-500 text-white"
                      : "bg-gray-400 text-white hover:bg-[#006674]"
                  } ${user?.role === "company" ? "pointer-events-none opacity-60" : ""}`}
                >
                  <FontAwesomeIcon icon={faHeart} />
                </button>

                <div className="w-full mt-2 p-2 flex justify-between items-center bg-[#006674] text-white">
                  <span>
                    <FontAwesomeIcon className="mr-1" icon={faCalendarAlt} />
                    {new Date(job.created_at).toLocaleDateString()}
                  </span>
                  <Link to={`/vacancy/${job.id}`} className="hover:underline">
                    {t("seemore") || "Подробнее"}
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Пагинация */}
      {totalPages > 1 && (
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => page > 1 && fetchJobs(page - 1)}
            disabled={page === 1}
            className={`px-4 py-2 rounded-lg ${
              page === 1 ? "bg-gray-400" : "bg-[#006674] hover:bg-[#005c5c]"
            } text-white transition`}
          >
            Назад
          </button>
          <span className="text-white font-semibold">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => page < totalPages && fetchJobs(page + 1)}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded-lg ${
              page === totalPages ? "bg-gray-400" : "bg-[#006674] hover:bg-[#005c5c]"
            } text-white transition`}
          >
            Далее
          </button>
        </div>
      )}
    </section>
  );
};

export default All_Highest_Sallary_Jobs;
